import { useState, useEffect } from 'react'
import { X, DollarSign } from 'lucide-react'
import { useGetCashInStatus, useClockIn } from '@/api/auth'
import { useSessionStore } from '@/stores/sessionStore'

interface CashInModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DENOMINATIONS = [
  { label: '$100', value: 100 },
  { label: '$50', value: 50 },
  { label: '$20', value: 20 },
  { label: '$10', value: 10 },
  { label: '$5', value: 5 },
  { label: '$2', value: 2 },
  { label: '$1', value: 1 },
  { label: '$0.25', value: 0.25 },
  { label: '$0.10', value: 0.10 },
  { label: '$0.05', value: 0.05 },
  { label: '$0.01', value: 0.01 },
]

export function CashInModal({ open, onOpenChange }: CashInModalProps) {
  const { staff } = useSessionStore()
  const { data: cashInStatus, isLoading } = useGetCashInStatus(open)
  const clockIn = useClockIn()

  // Prefer the BE-supplied personnel_in.full_name from the cash-in status
  // response over the session-store `staff.full_name`. The auth payload
  // that hydrates sessionStore.staff stores the login username (lowercase
  // "jeremy"), not the proper display name -- the BE has the proper name
  // and returns it on the cash-in status payload. Mirrors the same
  // pattern CashOutModal uses with `personnel_out_full_name`.
  const personnelInName =
    (cashInStatus as any)?.personnel_in?.full_name as string | undefined
    ?? staff?.full_name
    ?? '—'

  const [quantities, setQuantities] = useState<Record<number, number>>({})

  useEffect(() => {
    if (open) {
      // Reset quantities when modal opens
      setQuantities({})
    }
  }, [open])

  const handleQuantityChange = (value: number, qty: string) => {
    const quantity = parseInt(qty) || 0
    setQuantities(prev => ({
      ...prev,
      [value]: quantity,
    }))
  }

  const calculateTotal = () => {
    return Object.entries(quantities).reduce((sum, [value, qty]) => {
      return sum + (parseFloat(value) * qty)
    }, 0)
  }

  const handleSubmit = async () => {
    const total = calculateTotal()
    
    const moneys = DENOMINATIONS.map(denom => ({
      description: denom.label,
      value: denom.value,
      qty: quantities[denom.value] || 0,
    })).filter(m => m.qty > 0)

    try {
      await clockIn.mutateAsync({
        cashIn: { moneys },
        totalIn: total,
      })
      
      // Close modal first to prevent any re-render issues
      onOpenChange(false)
      // Then update session state
      useSessionStore.getState().clockIn()
    } catch (error) {
      console.error('Failed to clock in:', error)
    }
  }

  if (!open) return null

  const total = calculateTotal()
  const isAlreadyClockedIn = cashInStatus?.editable === false
  const previousCashIn = cashInStatus?.cash_in

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isAlreadyClockedIn ? 'Read-Only: Shift Drawer Count' : 'Cash IN'}
            </h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isAlreadyClockedIn ? (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {personnelInName} - {(cashInStatus as any)?.start ? new Date((cashInStatus as any).start).toLocaleString() : new Date().toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cash count was recorded at the start of your shift
                </p>
              </div>

              <div className="space-y-2">
                {DENOMINATIONS.map((denom) => {
                  const qty = (previousCashIn as any)?.moneys?.find((m: any) => m.value === denom.value)?.qty || 0
                  return (
                    <div key={denom.value} className="flex items-center gap-3">
                      <div className="w-16 text-right font-medium text-gray-700">
                        {denom.label}
                      </div>
                      <span className="text-gray-400">×</span>
                      <div className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-center text-gray-600">
                        {qty}
                      </div>
                      <span className="text-gray-400">=</span>
                      <div className="w-24 text-right font-medium text-gray-900">
                        ${(qty * denom.value).toFixed(2)}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 pt-4 border-t-2 border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-700">Total starting cash:</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${(previousCashIn as any)?.moneys?.reduce((sum: number, m: any) => sum + (m.value * m.qty), 0).toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  {personnelInName} - {new Date().toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Count the cash in your drawer to start your shift
                </p>
              </div>

              <div className="space-y-2">
                {DENOMINATIONS.map((denom) => (
                  <div key={denom.value} className="flex items-center gap-3">
                    <div className="w-16 text-right font-medium text-gray-700">
                      {denom.label}
                    </div>
                    <span className="text-gray-400">×</span>
                    <input
                      type="number"
                      min="0"
                      value={quantities[denom.value] || ''}
                      onChange={(e) => handleQuantityChange(denom.value, e.target.value)}
                      className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-0 outline-none text-center"
                      placeholder="0"
                    />
                    <span className="text-gray-400">=</span>
                    <div className="w-24 text-right font-medium text-gray-900">
                      ${((quantities[denom.value] || 0) * denom.value).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t-2 border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-700">Total starting cash:</span>
                  <span className="text-2xl font-bold text-green-600">${total.toFixed(2)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!isAlreadyClockedIn && !isLoading && (
          <div className="flex gap-2 p-5 border-t">
            <button
              onClick={() => onOpenChange(false)}
              disabled={clockIn.isPending}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={clockIn.isPending}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {clockIn.isPending ? 'Processing...' : 'OK'}
            </button>
          </div>
        )}
        {isAlreadyClockedIn && !isLoading && (
          <div className="flex gap-2 p-5 border-t">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-xl font-medium hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
